// The animated army markers (Oct 15 2026, Valor/Output/CLAUDE-ANIMATED-ARMY-HANDOFF-2026-10-15.md): the pure part — how
// big a squad is at a zoom, which banner a march carries, which way it faces, the stable footprint it needs, and the
// crowd resolver that turns overlapping squads into the arrows they were (and back once they have walked clear). No
// canvas here: render.ts draws from these answers and MapScreen.tsx paces the layer. Unit-tested in
// tests/unit/army.test.ts.
import type { March } from "./render";
import { ARMY } from "./armyData";

// ----------------------------------------------------------------------------------------------- the constants
/** the squad is about this many cells wide, within the clamp below (CSS px) — below REGION_MIN the realm keeps its arrows */
export const ARMY_CELLS = 0.9;
export const ARMY_MIN = 34;
export const ARMY_MAX = 64;
/** collision padding around the painted footprint (CSS px each side) */
export const ARMY_PAD = 4;
/** the clearance beyond the padded footprint a crowded march needs before its squad comes back… */
export const ARMY_CLEAR = 12;
/** …held for this long, continuously (ms) */
export const ARMY_RESTORE_MS = 250;
/** eight walking poses over one cycle, drawn at about this rate; the glow's pulse period */
export const ARMY_FPS = 12;
export const ARMY_CYCLE_MS = ARMY.cycleMs;
export const ARMY_GLOW_MS = 1500;
/** a route rising or falling at least this much steeper than it runs sideways is near-vertical: the squad keeps its
 *  painted (leftward) facing rather than turning on a few pixels of drift */
export const FACING_DEADBAND = 0.2;
/** The way the concept squad walks as painted: to the LEFT and a little away from the viewer (the soldiers' backs and the
 *  banner trailing behind them to the right). Travel the other way draws the art mirrored. (Ben, Oct 15 2026: the
 *  handoff had it facing right, and the first build walked backward.) */
export const PAINTED_FACING: Facing = "left";

export type Banner = "attack" | "support" | "return";
export type Facing = "right" | "left";
export interface Box { x: number; y: number; w: number; h: number }

// ----------------------------------------------------------------------------------------------- the squad
/** The squad's painted width at a zoom (CSS px): about ARMY_CELLS of a cell, never smaller than ARMY_MIN nor larger than
 *  ARMY_MAX. (The caller keeps arrows below REGION_MIN.) */
export function armySize(s: number): number {
  return Math.min(ARMY_MAX, Math.max(ARMY_MIN, ARMY_CELLS * s));
}

/** The banner cloth for a march's (normalised) kind — nothing for an unrecognised kind, which keeps its arrow. Ownership,
 *  direction and the original mission play no part: a return from an attack and from a support are both grey. */
export function bannerFor(kind: March["kind"]): Banner | null {
  return kind === "attack" || kind === "support" || kind === "return" ? kind : null;
}

/** Which way the squad faces for a route running (dx, dy) on screen (dx right, dy down): left as painted, right mirrored
 *  (`mirrored` says whether to flip the art). A near-vertical route (|dx| under FACING_DEADBAND of |dy|) keeps the
 *  previous facing, or the painted one. */
export function facing(dx: number, dy: number, prev: Facing | null = null): Facing {
  if (Math.abs(dx) < FACING_DEADBAND * Math.abs(dy) || (dx === 0 && dy === 0)) return prev ?? PAINTED_FACING;
  return dx > 0 ? "right" : "left";
}

/** Whether a facing needs the art flipped: only the facing the squad wasn't painted with. */
export const mirrored = (face: Facing) => face !== PAINTED_FACING;

/** The painted union of all eight frames about the ground-contact anchor, as shares of the union's width: how far it
 *  reaches left, right, up and down from the anchor, as painted (facing left: the banner trails to the right, so the
 *  squad reaches a little less far ahead of its anchor than behind it). From the 3x set (the 2x set is the same art). */
const SET = ARMY.sets["3x"];
const UW = SET.union.w;
export const REACH = {
  left: (SET.anchor[0] - SET.union.x) / UW,
  right: (SET.union.x + SET.union.w - SET.anchor[0]) / UW,
  up: (SET.anchor[1] - SET.union.y) / UW,
  down: (SET.union.y + SET.union.h - SET.anchor[1]) / UW,
} as const;

/** The stable footprint of a squad whose anchor is at (ax, ay) with painted width `size`, facing as `mirror` says, padded
 *  by ARMY_PAD: the same box for every frame and banner (the glow is not in it). */
export function footprint(ax: number, ay: number, size: number, mirror: boolean, pad = ARMY_PAD): Box {
  const left = (mirror ? REACH.right : REACH.left) * size;
  const right = (mirror ? REACH.left : REACH.right) * size;
  return { x: ax - left - pad, y: ay - REACH.up * size - pad, w: left + right + 2 * pad, h: (REACH.up + REACH.down) * size + 2 * pad };
}

/** A box grown by `d` on every side. */
export const grow = (b: Box, d: number): Box => ({ x: b.x - d, y: b.y - d, w: b.w + 2 * d, h: b.h + 2 * d });
export const overlaps = (a: Box, b: Box) => a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

// ----------------------------------------------------------------------------------------------- the crowd
export interface CrowdItem { id: string; box: Box }
export interface CrowdResult {
  /** the marches drawn as arrows this frame */
  arrows: Set<string>;
  /** a crowded march has walked clear and is waiting out ARMY_RESTORE_MS: the layer should keep drawing */
  pending: boolean;
}

/** Which marches show arrows. Every frame it is given every march that could be a squad, with the footprint its squad
 *  WOULD have now (even while it shows an arrow): two whose footprints overlap both become arrows, and a chain A–B–C
 *  becomes arrows as one group — the whole set is settled before anything is applied, so draw order and selection never
 *  decide which squad stays. Arrow mode starts the moment footprints meet; a squad comes back only once its footprint,
 *  grown by ARMY_CLEAR, has been clear of every other footprint for ARMY_RESTORE_MS without a break. Candidate pairs
 *  come from a grid the size of the biggest footprint, so a crowded view costs about its number of marches. */
export class CrowdResolver {
  /** when each crowded march's clearance began (null: not clear yet) */
  private crowded = new Map<string, number | null>();

  /** `now` is animation time (ms, any monotonic clock); the result is the same whatever order the items come in. */
  resolve(items: CrowdItem[], now: number): CrowdResult {
    const n = items.length;
    const order = items.map((_, i) => i).sort((a, b) => (items[a].id < items[b].id ? -1 : items[a].id > items[b].id ? 1 : 0));
    // candidate pairs from a grid of cells as big as the largest box
    let cell = 1;
    for (const it of items) cell = Math.max(cell, it.box.w, it.box.h);
    const grid = new Map<string, number[]>();
    const cellsOf = (b: Box) => {
      const x0 = Math.floor(b.x / cell), x1 = Math.floor((b.x + b.w) / cell);
      const y0 = Math.floor(b.y / cell), y1 = Math.floor((b.y + b.h) / cell);
      const out: string[] = [];
      for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) out.push(`${x},${y}`);
      return out;
    };
    for (const i of order) for (const k of cellsOf(items[i].box)) {
      const l = grid.get(k);
      if (l) l.push(i);
      else grid.set(k, [i]);
    }
    // union-find over overlapping pairs
    const parent = order.map((_, i) => i); // indexed by position in `order`
    const pos = new Map<number, number>(order.map((i, p) => [i, p]));
    const find = (p: number): number => { while (parent[p] !== p) { parent[p] = parent[parent[p]]; p = parent[p]; } return p; };
    const union = (a: number, b: number) => { const ra = find(a), rb = find(b); if (ra !== rb) parent[Math.max(ra, rb)] = Math.min(ra, rb); };
    const seen = new Set<string>();
    for (const list of grid.values()) {
      for (let a = 0; a < list.length; a++) for (let b = a + 1; b < list.length; b++) {
        const i = list[a], j = list[b];
        const key = i < j ? `${i}:${j}` : `${j}:${i}`;
        if (seen.has(key)) continue;
        seen.add(key);
        if (overlaps(items[i].box, items[j].box)) union(pos.get(i)!, pos.get(j)!);
      }
    }
    const size = new Map<number, number>();
    for (let p = 0; p < n; p++) { const r = find(p); size.set(r, (size.get(r) ?? 0) + 1); }
    const arrows = new Set<string>();
    let pending = false;
    const ids = new Set(items.map((it) => it.id));
    for (const [id] of this.crowded) if (!ids.has(id)) this.crowded.delete(id); // marches that are gone
    for (let p = 0; p < n; p++) {
      const it = items[order[p]];
      if (size.get(find(p))! >= 2) {
        this.crowded.set(it.id, null);
        arrows.add(it.id);
        continue;
      }
      if (!this.crowded.has(it.id)) continue; // a squad, and still one
      // crowded, now alone: clear of everyone by ARMY_CLEAR? (its grown box against every other padded box)
      const room = grow(it.box, ARMY_CLEAR);
      let clear = true;
      const near = new Set<number>();
      for (const k of cellsOf(room)) for (const j of grid.get(k) ?? []) if (j !== order[p]) near.add(j);
      for (const j of near) if (overlaps(room, items[j].box)) { clear = false; break; }
      if (!clear) {
        this.crowded.set(it.id, null);
        arrows.add(it.id);
        continue;
      }
      const since = this.crowded.get(it.id);
      if (since === null || since === undefined) {
        this.crowded.set(it.id, now);
        arrows.add(it.id);
        pending = true;
      } else if (now - since < ARMY_RESTORE_MS) {
        arrows.add(it.id);
        pending = true;
      } else {
        this.crowded.delete(it.id); // its squad is back
      }
    }
    return { arrows, pending };
  }

  /** Forget every march's state (the layer paused or the marches changed wholesale). */
  reset() {
    this.crowded.clear();
  }

  /** e2e checks: the ids in arrow mode */
  get inArrowMode(): string[] {
    return [...this.crowded.keys()].sort();
  }
}

// ----------------------------------------------------------------------------------------------- the walk
/** A stable phase per march (0–1), so neighbouring squads don't step in lockstep. */
export function phaseOf(id: string): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16777619);
  return ((h >>> 0) % 1000) / 1000;
}

/** The walking frame (0–7) at animation time `t` (ms) for a march with the given phase; frame 0 under reduced motion. */
export function frameAt(t: number, phase: number, reduced = false): number {
  if (reduced) return 0;
  const k = (((t / ARMY_CYCLE_MS + phase) % 1) + 1) % 1;
  return Math.min(ARMY.frames - 1, Math.floor(k * ARMY.frames));
}

/** The ground glow a squad carries: crimson under an attack, blue under support (Ben, Oct 15 2026), none under a return.
 *  The colours are the gradient's core, mid and rim (rgb), beside the kind's own line colour. */
export const GLOW_COLORS: Record<Exclude<Banner, "return">, { core: string; mid: string; rim: string }> = {
  attack: { core: "225,48,34", mid: "206,30,26", rim: "170,20,20" },
  support: { core: "92,150,240", mid: "66,126,222", rim: "40,90,190" },
};
export function glowFor(banner: Banner | null): Exclude<Banner, "return"> | null {
  return banner === "attack" || banner === "support" ? banner : null;
}

/** The glow's strength (0–1) at animation time `t`: a slow pulse, or steady under reduced motion. */
export function glowAt(t: number, phase: number, reduced = false): number {
  if (reduced) return 0.62;
  return 0.62 + 0.3 * Math.sin(2 * Math.PI * (t / ARMY_GLOW_MS + phase));
}

// ----------------------------------------------------------------------------------------------- the marches
/** The map's march kind for a movement's kind (game/city.ts MoveKind): both returns are `return`. */
export function marchKind(kind: string): March["kind"] {
  return kind === "attack" ? "attack" : kind === "support" ? "support" : kind.startsWith("return") ? "return" : "other";
}

/** Where a march runs from and to. The game keeps a returning movement's origin and target as they were (world-process
 *  Village.cancelSend and the returning movements CombatUtils makes: origin = the army's home, target = the city it is
 *  "Returning from"), while the army itself walks the other way — so a return's ends are swapped. An outgoing march
 *  runs from home to the other city; an incoming one from the other city to home. */
export function marchEnds(dir: "in" | "out", home: [number, number], other: [number, number], kind: March["kind"]): { from: [number, number]; to: [number, number] } {
  const [a, b] = dir === "out" ? [home, other] : [other, home];
  return kind === "return" ? { from: b, to: a } : { from: a, to: b };
}
